class BBShowMoreOptionValues extends HTMLElement {
  constructor() {
    super();
    this.maxVisibleOptions = 5;
    this.showMoreBtn = null;
    this.showMoreInput = null;
    this.optionElements = [];
    this.isExpanded = false;
    this.storageKey = null;
    this.boundEventHandlers = new Map();
  }

  connectedCallback() {
    // Wait for DOM to be fully constructed
    requestAnimationFrame(() => {
      this.init();
    });

    // Setup page unload handler
    this.setupPageUnloadHandler();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }
  init() {
    try {
      this.createStorageKey();
      this.findElements();
      this.restoreExpandedState();
      this.checkAutoExpansion();

      if (!this.shouldShowButton()) {
        this.hideButton();
        return;
      }

      this.showButton();
      this.setupEventListeners();
      this.updateVisibility();
    } catch (error) {
      console.error('BBShowMoreOptionValues: Error during initialization:', error);
    }
  }

  createStorageKey() {
    const sectionId = this.closest('[data-section-id]')?.dataset.sectionId || 'unknown';
    const productHandle = this.closest('variant-picker')?.getAttribute('handle') || 'unknown';
    const optionPosition = Array.from(this.closest('variant-picker')?.querySelectorAll('.variant-picker__option') || []).indexOf(this.closest('.variant-picker__option')) + 1;
    this.storageKey = `bb-show-more-${sectionId}-${productHandle}-option-${optionPosition}`;
  }

  findElements() {
    // Get all option value label elements
    this.optionElements = Array.from(this.children).filter((child) => child.tagName.toLowerCase() === 'label' && !child.classList.contains('show-more-btn'));

    this.showMoreBtn = this.querySelector('.show-more-btn');
    this.showMoreInput = this.querySelector('.show-more-input');
  }

  restoreExpandedState() {
    try {
      this.isExpanded = this.getStorageValue(this.storageKey) === 'true';
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to restore state from localStorage:', error);
      this.isExpanded = false;
    }
  }

  shouldShowButton() {
    return this.showMoreBtn && this.optionElements.length > this.maxVisibleOptions;
  }

  hideButton() {
    this.toggleElementDisplay(this.showMoreBtn, false);
    this.toggleElementDisplay(this.showMoreInput, false);
  }

  showButton() {
    this.toggleElementDisplay(this.showMoreBtn, true);
    this.toggleElementDisplay(this.showMoreInput, true);
  }

  toggleElementDisplay(element, show) {
    if (element) {
      element.style.display = show ? '' : 'none';
    }
  }
  setupEventListeners() {
    const handleButtonClick = (e) => {
      e.preventDefault();
      this.toggleExpanded();
    };

    const handleInputChange = (e) => {
      e.preventDefault();
      // Uncheck the checkbox immediately to prevent it from staying checked
      this.showMoreInput.checked = false;
      this.toggleExpanded();
    };

    if (this.showMoreBtn) {
      this.showMoreBtn.addEventListener('click', handleButtonClick);
      this.boundEventHandlers.set('buttonClick', handleButtonClick);
    }

    if (this.showMoreInput) {
      this.showMoreInput.addEventListener('change', handleInputChange);
      this.boundEventHandlers.set('inputChange', handleInputChange);
    }
  }

  removeEventListeners() {
    if (this.showMoreBtn && this.boundEventHandlers.has('buttonClick')) {
      this.showMoreBtn.removeEventListener('click', this.boundEventHandlers.get('buttonClick'));
    }

    if (this.showMoreInput && this.boundEventHandlers.has('inputChange')) {
      this.showMoreInput.removeEventListener('change', this.boundEventHandlers.get('inputChange'));
    }

    this.boundEventHandlers.clear();
  }
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    this.saveExpandedState();
    this.updateVisibility();
  }

  saveExpandedState() {
    try {
      if (this.storageKey) {
        this.setStorageValue(this.storageKey, this.isExpanded.toString());
      }
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to save state to localStorage:', error);
    }
  }
  updateVisibility() {
    if (this.optionElements.length <= this.maxVisibleOptions) {
      return;
    }

    const remainingCount = this.optionElements.length - this.maxVisibleOptions;

    // Update visibility for hidden elements
    this.optionElements.forEach((labelElement, index) => {
      if (index >= this.maxVisibleOptions) {
        this.toggleOptionVisibility(labelElement, this.isExpanded);
      }
    });

    // Update button state and text
    this.updateButtonState(remainingCount);

    // Position button after 5th visible option when collapsed
    this.positionButton();
  }

  toggleOptionVisibility(labelElement, show) {
    const inputElement = labelElement.querySelector('input') || labelElement.previousElementSibling;

    labelElement.style.display = show ? '' : 'none';
    if (inputElement && inputElement.tagName.toLowerCase() === 'input') {
      inputElement.style.display = show ? '' : 'none';
    }
  }

  updateButtonState(remainingCount) {
    if (!this.isExpanded) {
      const buttonText = this.showMoreBtn?.querySelector('span');
      if (buttonText) {
        buttonText.textContent = `+${remainingCount}`;
      }
      this.showButton();
    } else {
      this.hideButton();
    }
  }

  positionButton() {
    if (!this.isExpanded && this.optionElements.length > this.maxVisibleOptions && this.showMoreBtn) {
      const fifthOption = this.optionElements[this.maxVisibleOptions - 1];
      if (fifthOption && fifthOption.nextElementSibling !== this.showMoreBtn) {
        fifthOption.insertAdjacentElement('afterend', this.showMoreBtn);
      }
    }
  }
  setupPageUnloadHandler() {
    // Only set up handlers once per page
    if (!window.bbShowMoreUnloadHandlerSet) {
      window.bbShowMoreUnloadHandlerSet = true;

      const sessionKey = 'bb-show-more-session';

      try {
        const currentSession = sessionStorage.getItem(sessionKey);

        if (!currentSession) {
          // New session, clear any old localStorage entries
          this.clearAllShowMoreStorage();
          sessionStorage.setItem(sessionKey, Date.now().toString());
        }

        // Clear localStorage when the tab is closed or navigated away from
        window.addEventListener('beforeunload', () => {
          try {
            sessionStorage.removeItem(sessionKey);
          } catch (error) {
            console.warn('BBShowMoreOptionValues: Failed to clear session marker:', error);
          }
        });
      } catch (error) {
        console.warn('BBShowMoreOptionValues: Failed to setup page unload handler:', error);
      }
    }
  }

  clearAllShowMoreStorage() {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('bb-show-more-')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to clear localStorage:', error);
    }
  }
  checkAutoExpansion() {
    // Check if any selected option values are in the hidden section
    const hasSelectedHiddenOption = this.optionElements.some((labelElement, index) => {
      if (index >= this.maxVisibleOptions) {
        const inputElement = this.findInputElement(labelElement);
        return inputElement && inputElement.checked;
      }
      return false;
    });

    if (hasSelectedHiddenOption) {
      this.isExpanded = true;
      this.saveExpandedState();
    }
  }

  findInputElement(labelElement) {
    return labelElement.querySelector('input') || labelElement.previousElementSibling;
  }

  // Storage utilities with error handling
  getStorageValue(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('BBShowMoreOptionValues: localStorage access failed:', error);
      return null;
    }
  }

  setStorageValue(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('BBShowMoreOptionValues: localStorage write failed:', error);
    }
  }

  clearAllShowMoreStorage() {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('bb-show-more-')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to clear localStorage:', error);
    }
  }
}
customElements.define('bb-show-more-option-values', BBShowMoreOptionValues);

class BBInlineSearch extends HTMLElement {
  constructor() {
    super();
    this.isMenuOpen = false;
    this.searchInput = null;
    this.megaMenu = null;
    this.searchSubmit = null;
    this.form = null;
    this.predictiveSearch = null;
    this.mutationObserver = null;
    this.boundEventHandlers = new Map();

    // Bind methods once to avoid memory leaks
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    try {
      this.findElements();

      if (!this.searchInput || !this.megaMenu) {
        console.warn('BBInlineSearch: Required elements not found');
        return;
      }

      this.setupEventListeners();
    } catch (error) {
      console.error('BBInlineSearch: Error during initialization:', error);
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.cleanupObserver();
  }

  findElements() {
    this.searchInput = this.querySelector('input[name="q"]');
    this.megaMenu = this.querySelector('#bb-inline-search-mega-menu');
    this.searchSubmit = this.querySelector('[data-search-submit]');
    this.form = this.querySelector('form');
    this.predictiveSearch = this.querySelector('predictive-search');
  }
  setupEventListeners() {
    const handleInputChange = this.handleInputChange.bind(this);
    const handleInputFocus = this.handleInputFocus.bind(this);
    const handleMegaMenuClick = this.handleMegaMenuClick.bind(this);
    const handleSearchSubmit = this.handleSearchSubmit.bind(this);

    // Store bound handlers for cleanup
    this.boundEventHandlers.set('inputChange', handleInputChange);
    this.boundEventHandlers.set('inputFocus', handleInputFocus);
    this.boundEventHandlers.set('megaMenuClick', handleMegaMenuClick);
    this.boundEventHandlers.set('searchSubmit', handleSearchSubmit);

    // Listen for input changes and focus
    this.searchInput.addEventListener('input', handleInputChange);
    this.searchInput.addEventListener('focus', handleInputFocus);

    // Listen for clicks on the mega menu to prevent closing
    this.megaMenu.addEventListener('click', handleMegaMenuClick);

    // Handle search submit button
    if (this.searchSubmit) {
      this.searchSubmit.addEventListener('click', handleSearchSubmit);
    }

    // Listen for predictive search changes
    this.setupPredictiveSearchObserver();
  }

  setupPredictiveSearchObserver() {
    if (this.predictiveSearch) {
      this.mutationObserver = new MutationObserver(this.handlePredictiveSearchChange.bind(this));
      this.mutationObserver.observe(this.predictiveSearch, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }

  cleanupObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }
  removeEventListeners() {
    if (this.searchInput) {
      const inputChangeHandler = this.boundEventHandlers.get('inputChange');
      const inputFocusHandler = this.boundEventHandlers.get('inputFocus');

      if (inputChangeHandler) {
        this.searchInput.removeEventListener('input', inputChangeHandler);
      }
      if (inputFocusHandler) {
        this.searchInput.removeEventListener('focus', inputFocusHandler);
      }
    }

    if (this.megaMenu) {
      const megaMenuClickHandler = this.boundEventHandlers.get('megaMenuClick');
      if (megaMenuClickHandler) {
        this.megaMenu.removeEventListener('click', megaMenuClickHandler);
      }
    }

    if (this.searchSubmit) {
      const searchSubmitHandler = this.boundEventHandlers.get('searchSubmit');
      if (searchSubmitHandler) {
        this.searchSubmit.removeEventListener('click', searchSubmitHandler);
      }
    }

    document.removeEventListener('click', this.handleClickOutside);
    this.boundEventHandlers.clear();
  }
  handleInputChange() {
    const inputValue = this.searchInput.value.trim();
    this.toggleMegaMenu(inputValue.length > 0);
  }

  handleInputFocus() {
    const inputValue = this.searchInput.value.trim();
    if (inputValue.length > 0) {
      this.openMegaMenu();
    }
  }

  handleMegaMenuClick(event) {
    // Prevent closing when clicking inside the mega menu
    event.stopPropagation();
  }

  handleSearchSubmit(event) {
    event.preventDefault();
    const searchValue = this.searchInput.value.trim();

    if (this.form && searchValue !== '') {
      this.form.submit();
    }
  }

  handlePredictiveSearchChange() {
    const inputValue = this.searchInput.value.trim();

    if (inputValue.length > 0 && this.predictiveSearch) {
      const hasResults = this.predictiveSearch.querySelector('[slot="results"]')?.children.length > 0;
      const hasIdle = this.predictiveSearch.querySelector('[slot="idle"]')?.children.length > 0;

      if (hasResults || hasIdle) {
        this.openMegaMenu();
      }
    }
  }

  handleClickOutside(event) {
    // Close mega menu if clicking outside the search input or mega menu
    if (!this.contains(event.target)) {
      this.closeMegaMenu();
    }
  }

  toggleMegaMenu(shouldOpen) {
    if (shouldOpen) {
      this.openMegaMenu();
    } else {
      this.closeMegaMenu();
    }
  }
  openMegaMenu() {
    if (this.isMenuOpen) return;

    this.isMenuOpen = true;

    // Show the mega menu using CSS class
    this.megaMenu.classList.add('is-open');
    this.megaMenu.setAttribute('open', '');

    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);

    // Trigger predictive search if needed
    this.triggerPredictiveSearch();

    // Dispatch custom event for header transparency check
    this.dispatchCustomEvent('bb:header-check-transparency');
  }

  closeMegaMenu() {
    if (!this.isMenuOpen) return;

    this.isMenuOpen = false;

    // Hide the mega menu using CSS class
    this.megaMenu.classList.remove('is-open');
    this.megaMenu.removeAttribute('open');

    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);

    // Dispatch custom event for header transparency check
    this.dispatchCustomEvent('bb:header-check-transparency');
  }

  triggerPredictiveSearch() {
    if (this.predictiveSearch && this.searchInput) {
      try {
        const inputEvent = new Event('input', { bubbles: true });
        this.searchInput.dispatchEvent(inputEvent);
      } catch (error) {
        console.warn('BBInlineSearch: Failed to trigger predictive search:', error);
      }
    }
  }

  dispatchCustomEvent(eventName) {
    try {
      document.dispatchEvent(new CustomEvent(eventName));
    } catch (error) {
      console.warn(`BBInlineSearch: Failed to dispatch ${eventName} event:`, error);
    }
  }
}

customElements.define('bb-inline-search', BBInlineSearch);

class BBDeliveryTime extends HTMLElement {
  constructor() {
    super();
    this.minDays = 2;
    this.maxDays = 5;
    this.thresholdHour = 14;
    this.thresholdMinute = 0;
    this.localeText = '';
    this.fallbackText = '';
    this.hourSingular = 'hour';
    this.hourPlural = 'hours';
    this.minuteSingular = 'minute';
    this.minutePlural = 'minutes';
    this.andConnector = 'and';
    this.updateInterval = null;
    this.timeZone = 'Europe/Berlin';
  }
  connectedCallback() {
    try {
      // Block is already hidden via inline styles in HTML
      // Only show it after successful calculation

      this.parseAttributes();
      this.initializeDisplay();
      this.startUpdateInterval();
    } catch (error) {
      console.error('BBDeliveryTime: Error during initialization:', error);
      this.showFallbackDisplay();
    }
  }

  disconnectedCallback() {
    this.clearUpdateInterval();
  }
  // Add method to show fallback display when initialization fails
  showFallbackDisplay() {
    const proseDiv = this.querySelector('.prose p');
    if (proseDiv) {
      proseDiv.innerHTML = this.fallbackText || 'Delivery in 2-5 business days';
    }
    // Show the block after setting fallback content
    this.showBlock();
  }

  // Method to show the block with smooth transition
  showBlock() {
    this.style.visibility = 'visible';
    this.style.opacity = '1';
    this.style.transition = 'opacity 0.3s ease-in-out';
  }
  parseAttributes() {
    // Default fallback values
    const defaultMinDays = 2;
    const defaultMaxDays = 5;
    const defaultThresholdHour = 14;
    const defaultThresholdMinute = 0;

    // Parse and validate delivery days
    const rawMinDays = parseInt(this.getAttribute('data-min-days'));
    const rawMaxDays = parseInt(this.getAttribute('data-max-days'));

    if (isNaN(rawMinDays) || rawMinDays < 1 || rawMinDays > 30) {
      console.error('BBDeliveryTime: Invalid min delivery days value. Using fallback value of', defaultMinDays);
      this.minDays = defaultMinDays;
    } else {
      this.minDays = rawMinDays;
    }

    if (isNaN(rawMaxDays) || rawMaxDays < 1 || rawMaxDays > 30) {
      console.error('BBDeliveryTime: Invalid max delivery days value. Using fallback value of', defaultMaxDays);
      this.maxDays = defaultMaxDays;
    } else {
      this.maxDays = rawMaxDays;
    }

    // Ensure min days is not greater than max days
    if (this.minDays > this.maxDays) {
      console.error('BBDeliveryTime: Min delivery days cannot be greater than max delivery days. Using fallback values.');
      this.minDays = defaultMinDays;
      this.maxDays = defaultMaxDays;
    }

    // Parse and validate threshold hour
    const rawThresholdHour = parseInt(this.getAttribute('data-threshold-hour'));
    if (isNaN(rawThresholdHour) || rawThresholdHour < 0 || rawThresholdHour > 23) {
      console.error('BBDeliveryTime: Invalid threshold hour value (must be 0-23). Using fallback value of', defaultThresholdHour);
      this.thresholdHour = defaultThresholdHour;
    } else {
      this.thresholdHour = rawThresholdHour;
    }

    // Parse and validate threshold minute
    const rawThresholdMinute = parseInt(this.getAttribute('data-threshold-minute'));
    if (isNaN(rawThresholdMinute) || rawThresholdMinute < 0 || rawThresholdMinute > 59) {
      console.error('BBDeliveryTime: Invalid threshold minute value (must be 0-59). Using fallback value of', defaultThresholdMinute);
      this.thresholdMinute = defaultThresholdMinute;
    } else {
      this.thresholdMinute = rawThresholdMinute;
    }

    // Parse locale texts with validation
    this.localeText =
      this.getAttribute('data-locale-text') ||
      'Order within the next <strong>{{ remaining_time }}</strong> and receive delivery between <strong>{{ min_date }} and {{ max_date }}</strong>';
    this.fallbackText = this.getAttribute('data-fallback-text') || 'Delivery in 2-5 business days';
    this.hourSingular = this.getAttribute('data-hour-singular') || 'hour';
    this.hourPlural = this.getAttribute('data-hour-plural') || 'hours';
    this.minuteSingular = this.getAttribute('data-minute-singular') || 'minute';
    this.minutePlural = this.getAttribute('data-minute-plural') || 'minutes';
    this.andConnector = this.getAttribute('data-and-connector') || 'and';

    // Validate that required placeholders exist in locale text
    if (!this.localeText.includes('{{ remaining_time }}') || !this.localeText.includes('{{ min_date }}') || !this.localeText.includes('{{ max_date }}')) {
      console.error('BBDeliveryTime: Locale text is missing required placeholders. Using fallback text.');
      this.localeText = 'Order within the next <strong>{{ remaining_time }}</strong> and receive delivery between <strong>{{ min_date }} and {{ max_date }}</strong>';
    }

    // Validate that locale strings are not empty
    if (!this.hourSingular.trim()) {
      console.error('BBDeliveryTime: Hour singular text is empty. Using fallback.');
      this.hourSingular = 'hour';
    }
    if (!this.hourPlural.trim()) {
      console.error('BBDeliveryTime: Hour plural text is empty. Using fallback.');
      this.hourPlural = 'hours';
    }
    if (!this.minuteSingular.trim()) {
      console.error('BBDeliveryTime: Minute singular text is empty. Using fallback.');
      this.minuteSingular = 'minute';
    }
    if (!this.minutePlural.trim()) {
      console.error('BBDeliveryTime: Minute plural text is empty. Using fallback.');
      this.minutePlural = 'minutes';
    }
    if (!this.andConnector.trim()) {
      console.error('BBDeliveryTime: And connector text is empty. Using fallback.');
      this.andConnector = 'and';
    }
  }
  initializeDisplay() {
    // Validate DOM structure before attempting to display
    if (!this.validateDOMStructure()) {
      console.error('BBDeliveryTime: Invalid DOM structure, using fallback');
      this.showFallbackDisplay();
      return;
    }

    this.updateDeliveryTimeDisplay();
  }
  startUpdateInterval() {
    // Update every minute
    this.updateInterval = setInterval(() => {
      this.updateDeliveryTimeDisplay();
    }, 60000);

    // Also update immediately on visibility change (when user switches tabs)
    this.handleVisibilityChange = () => {
      if (!document.hidden) {
        this.updateDeliveryTimeDisplay();
      }
    };

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  clearUpdateInterval() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.handleVisibilityChange = null;
    }
  }
  calculateNextThreshold() {
    try {
      const now = new Date();

      // Get current time in Berlin timezone
      const nowInBerlin = new Date(
        now.toLocaleString('en-US', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
      );

      // Validate Berlin time
      if (isNaN(nowInBerlin.getTime())) {
        throw new Error('Invalid Berlin time calculation');
      }

      // Create threshold for today in Berlin time
      const todayThresholdBerlin = new Date(nowInBerlin);
      todayThresholdBerlin.setHours(this.thresholdHour, this.thresholdMinute, 0, 0);

      // Calculate the offset between Berlin time and local time
      const berlinOffset = nowInBerlin.getTime() - now.getTime();

      // Convert Berlin threshold back to local time for countdown calculation
      const todayThresholdLocal = new Date(todayThresholdBerlin.getTime() - berlinOffset);

      // Validate threshold date
      if (isNaN(todayThresholdLocal.getTime())) {
        throw new Error('Invalid threshold time calculation');
      }

      // If current local time is before today's threshold (in local time), use today's threshold
      if (now < todayThresholdLocal) {
        return todayThresholdLocal;
      } else {
        // Otherwise, use next business day's threshold
        const nextBusinessDayBerlin = this.getNextBusinessDay(nowInBerlin);
        nextBusinessDayBerlin.setHours(this.thresholdHour, this.thresholdMinute, 0, 0);

        // Convert back to local time
        const nextThresholdLocal = new Date(nextBusinessDayBerlin.getTime() - berlinOffset);

        if (isNaN(nextThresholdLocal.getTime())) {
          throw new Error('Invalid next business day threshold calculation');
        }

        return nextThresholdLocal;
      }
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating next threshold:', error);

      // Fallback: return a valid date 24 hours from now
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() + 1);
      fallbackDate.setHours(14, 0, 0, 0); // Default 14:00 threshold
      return fallbackDate;
    }
  }
  calculateRemainingTime(targetTime) {
    try {
      const now = new Date(); // Use local time since targetTime is already in local time

      // Validate dates
      if (isNaN(now.getTime()) || isNaN(targetTime.getTime())) {
        throw new Error('Invalid date values in remaining time calculation');
      }

      const timeDiff = targetTime - now;

      // If time has passed, show next threshold
      if (timeDiff <= 0) {
        return `1 ${this.minuteSingular}`;
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      // Validate calculated values
      if (hours < 0 || minutes < 0) {
        throw new Error('Negative time values calculated');
      }

      // Format time string
      return this.formatTimeString(hours, minutes);
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating remaining time:', error);
      // Fallback: return a generic time string
      return `1 ${this.hourSingular}`;
    }
  }

  // Helper method to format time strings consistently
  formatTimeString(hours, minutes) {
    if (hours > 0) {
      const hoursText = hours === 1 ? this.hourSingular : this.hourPlural;
      if (minutes > 0) {
        const minutesText = minutes === 1 ? this.minuteSingular : this.minutePlural;
        return `${hours} ${hoursText} ${this.andConnector} ${minutes} ${minutesText}`;
      } else {
        return `${hours} ${hoursText}`;
      }
    } else {
      const minutesText = minutes === 1 ? this.minuteSingular : this.minutePlural;
      return `${Math.max(1, minutes)} ${minutesText}`;
    }
  }
  getNextBusinessDay(date) {
    try {
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided to getNextBusinessDay');
      }

      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);

      // Prevent infinite loop by limiting iterations
      let iterations = 0;
      const maxIterations = 10; // Should never need more than 7 to skip weekends

      // Skip weekends
      while ((nextDay.getDay() === 0 || nextDay.getDay() === 6) && iterations < maxIterations) {
        nextDay.setDate(nextDay.getDate() + 1);
        iterations++;
      }

      if (iterations >= maxIterations) {
        console.error('BBDeliveryTime: Too many iterations finding next business day');
        // Return Monday if we can't find a business day
        const monday = new Date(date);
        monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7));
        return monday;
      }

      if (isNaN(nextDay.getTime())) {
        throw new Error('Invalid next business day calculation result');
      }

      return nextDay;
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating next business day:', error);

      // Fallback: return tomorrow
      const fallback = new Date(date);
      fallback.setDate(fallback.getDate() + 1);
      return fallback;
    }
  }
  calculateDeliveryDates() {
    try {
      const now = new Date();

      // Get current time in Berlin timezone for delivery date calculation
      const nowInBerlin = new Date(
        now.toLocaleString('en-US', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
      );

      // Create threshold for today in Berlin time
      const todayThresholdBerlin = new Date(nowInBerlin);
      todayThresholdBerlin.setHours(this.thresholdHour, this.thresholdMinute, 0, 0);

      // Validate dates
      if (isNaN(nowInBerlin.getTime()) || isNaN(todayThresholdBerlin.getTime())) {
        throw new Error('Invalid dates in delivery calculation');
      }

      // Validate delivery days are reasonable
      if (this.minDays < 1 || this.maxDays < 1 || this.minDays > 100 || this.maxDays > 100) {
        throw new Error('Invalid delivery days configuration');
      }

      // Determine if we should use today or tomorrow as the base date (all in Berlin time)
      let baseDate;
      if (nowInBerlin < todayThresholdBerlin) {
        // Current Berlin time is before threshold, so delivery starts from today + min days
        baseDate = new Date(nowInBerlin);
      } else {
        // Current Berlin time is after threshold, so delivery starts from tomorrow + min days
        baseDate = new Date(nowInBerlin);
        baseDate.setDate(baseDate.getDate() + 1);
      }

      if (isNaN(baseDate.getTime())) {
        throw new Error('Invalid base date calculation');
      }

      // Skip weekends for business days calculation
      const minDeliveryDate = this.addBusinessDays(baseDate, this.minDays);
      const maxDeliveryDate = this.addBusinessDays(baseDate, this.maxDays);

      // Validate delivery dates
      if (isNaN(minDeliveryDate.getTime()) || isNaN(maxDeliveryDate.getTime())) {
        throw new Error('Invalid delivery dates calculated');
      }

      return {
        minDate: this.formatDate(minDeliveryDate),
        maxDate: this.formatDate(maxDeliveryDate),
      };
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating delivery dates:', error);

      // Fallback: return generic dates 2-5 days from now
      const today = new Date();
      const minFallback = new Date(today);
      minFallback.setDate(today.getDate() + 2);
      const maxFallback = new Date(today);
      maxFallback.setDate(today.getDate() + 5);

      return {
        minDate: this.formatDate(minFallback),
        maxDate: this.formatDate(maxFallback),
      };
    }
  }
  addBusinessDays(startDate, days) {
    try {
      if (isNaN(startDate.getTime())) {
        throw new Error('Invalid start date provided to addBusinessDays');
      }

      if (days < 0 || days > 100) {
        throw new Error(`Invalid days value: ${days}. Must be between 0 and 100.`);
      }

      const result = new Date(startDate);
      let daysAdded = 0;
      let iterations = 0;
      const maxIterations = days * 2 + 10; // Reasonable upper limit to prevent infinite loops

      while (daysAdded < days && iterations < maxIterations) {
        result.setDate(result.getDate() + 1);
        iterations++;

        // Validate the date is still valid
        if (isNaN(result.getTime())) {
          throw new Error('Invalid date during business days calculation');
        }

        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (result.getDay() !== 0 && result.getDay() !== 6) {
          daysAdded++;
        }
      }

      if (iterations >= maxIterations) {
        console.error('BBDeliveryTime: Too many iterations in addBusinessDays');
        // Fallback: just add the days without business day logic
        const fallback = new Date(startDate);
        fallback.setDate(startDate.getDate() + days);
        return fallback;
      }

      return result;
    } catch (error) {
      console.error('BBDeliveryTime: Error adding business days:', error);

      // Fallback: just add the days without business day logic
      const fallback = new Date(startDate);
      fallback.setDate(startDate.getDate() + days);
      return fallback;
    }
  }

  formatDate(date) {
    try {
      // Format as DD.MM. (German format)
      const day = date.getDate();
      const month = date.getMonth() + 1;
      return `${day}.${month}.`;
    } catch (error) {
      console.error('BBDeliveryTime: Error formatting date:', error);
      return 'N/A';
    }
  }
  updateDeliveryTimeDisplay() {
    try {
      const nextThreshold = this.calculateNextThreshold();
      const remainingTime = this.calculateRemainingTime(nextThreshold);
      const deliveryDates = this.calculateDeliveryDates();

      // Replace placeholders in the locale text
      let displayText = this.localeText
        .replace('{{ remaining_time }}', remainingTime)
        .replace('{{ min_date }}', deliveryDates.minDate)
        .replace('{{ max_date }}', deliveryDates.maxDate);

      // Find the prose div and update it
      const proseDiv = this.querySelector('.prose p');
      if (proseDiv) {
        proseDiv.innerHTML = displayText;
        // Show the block now that we have the proper content
        this.showBlock();
      } else {
        console.warn('BBDeliveryTime: Could not find .prose p element for display update');
      }
    } catch (error) {
      console.error('BBDeliveryTime: Error updating display:', error);
      this.showFallbackDisplay();
    }
  }

  // Add method to validate DOM structure
  validateDOMStructure() {
    const proseDiv = this.querySelector('.prose p');
    if (!proseDiv) {
      console.warn('BBDeliveryTime: Required DOM structure (.prose p) not found');
      return false;
    }
    return true;
  }
}

customElements.define('bb-delivery-time', BBDeliveryTime);

class BBSondermasse extends HTMLElement {
  constructor() {
    super();
    this.variantId = null;
    this.preisProQm = null;
    this.formId = null;
    this.moneyFormat = null;
    this.currencyCodeEnabled = false;
  }

  connectedCallback() {
    try {
      this.parseAttributes();
      this.setupEventListeners();
      this.setupFormValidation();
    } catch (error) {
      console.error('BBSondermasse: Error during initialization:', error);
    }
  }

  parseAttributes() {
    this.variantId = this.getAttribute('data-variant-id');
    this.preisProQm = parseFloat(this.getAttribute('data-preis-pro-qm'));
    this.formId = this.getAttribute('data-form-id');
    this.moneyFormat = this.getAttribute('data-money-format') || '€{{amount}}';
    this.currencyCodeEnabled = this.getAttribute('data-currency-code-enabled') === 'true';

    // Validation
    if (!this.variantId || isNaN(this.preisProQm) || !this.formId) {
      throw new Error('BBSondermasse: Missing required attributes');
    }
  }

  setupEventListeners() {
    const inputs = this.querySelectorAll('input[data-type]');
    inputs.forEach((input) => {
      input.addEventListener('input', () => this.calculatePrice());
      input.addEventListener('blur', (e) => this.snapToConstraints(e.target));
    });
  }

  setupFormValidation() {
    const form = document.getElementById(this.formId);
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  calculatePrice() {
    const laenge = this.getInputValue('laenge');
    const breite = this.getInputValue('breite');

    this.hideError();

    if (laenge > 0 && breite > 0 && this.validateRange(laenge, breite)) {
      const sqm = (laenge * breite) / 10000;
      const price = sqm * this.preisProQm;

      this.updatePriceDisplay(laenge, breite, sqm, price);
      this.updateCartProperty(price);
    } else {
      this.hidePriceDisplay();
      this.clearCartProperty();
    }
  }

  snapToConstraints(input) {
    const value = parseFloat(input.value);
    if (isNaN(value)) return;

    const min = parseFloat(input.getAttribute('min'));
    const max = parseFloat(input.getAttribute('max'));
    const step = parseFloat(input.getAttribute('step')) || 1;

    let snappedValue = value;

    if (!isNaN(min) && value < min) {
      snappedValue = min;
    } else if (!isNaN(max) && value > max) {
      snappedValue = max;
    } else {
      snappedValue = Math.round(value / step) * step;
    }

    if (snappedValue !== value) {
      input.value = snappedValue;
      this.calculatePrice();
    }

    this.validateField(input);
  }

  validateField(input) {
    const value = parseFloat(input.value) || 0;

    if (value > 0 && !this.isInRange(input, value)) {
      input.classList.add('is-invalid');
      console.log('add is-invalid to', input);
    } else {
      input.classList.remove('is-invalid');
      console.log('remove is-invalid from', input);
    }

    const laenge = this.getInputValue('laenge');
    const breite = this.getInputValue('breite');

    if (laenge > 0 && breite > 0 && !this.validateRange(laenge, breite)) {
      this.showError();
    } else {
      this.hideError();
    }
  }

  clearFieldErrors() {
    console.log('clearFieldErrors');
    const inputs = this.querySelectorAll('input[data-type]');
    inputs.forEach((input) => input.classList.remove('is-invalid'));
  }

  getInputValue(type) {
    const input = this.querySelector(`input[data-type="${type}"]`);
    return input ? parseFloat(input.value) || 0 : 0;
  }

  validateRange(laenge, breite) {
    const laengeInput = this.querySelector('input[data-type="laenge"]');
    const breiteInput = this.querySelector('input[data-type="breite"]');
    return this.isInRange(laengeInput, laenge) && this.isInRange(breiteInput, breite);
  }

  isInRange(input, value) {
    if (!input) return false;
    const min = parseFloat(input.getAttribute('min'));
    const max = parseFloat(input.getAttribute('max'));
    return (!isNaN(min) ? value >= min : true) && (!isNaN(max) ? value <= max : true);
  }

  updatePriceDisplay(laenge, breite, sqm, price) {
    const display = this.querySelector('[data-sondermasse="price-display"]');
    const amount = this.querySelector('[data-sondermasse="price-amount"]');
    const dimensions = this.querySelector('[data-sondermasse="dimensions"]');
    const meters = this.querySelector('[data-sondermasse="square-meters"]');

    if (display && amount) {
      display.style.display = 'block';
      amount.textContent = this.formatPrice(price);

      if (dimensions) dimensions.textContent = `${breite} cm × ${laenge} cm`;
      if (meters) meters.textContent = ` (${sqm.toFixed(2).replace('.', ',')} m²)`;
    }
  }

  hidePriceDisplay() {
    const display = this.querySelector('[data-sondermasse="price-display"]');
    if (display) display.style.display = 'none';
  }

  updateCartProperty(price) {
    const hiddenInput = this.querySelector('[data-price-property]');
    if (hiddenInput) {
      const cartProperty = [
        {
          variantId: this.variantId,
          quantityPerQuantity: 1,
          overrideAbsolutePrice: Math.round(price * 100) / 100,
        },
      ];
      hiddenInput.value = JSON.stringify(cartProperty);
    }
  }

  clearCartProperty() {
    const hiddenInput = this.querySelector('[data-price-property]');
    if (hiddenInput) hiddenInput.value = '';
  }

  formatPrice(price) {
    try {
      const amount = price.toFixed(2).replace('.', ',');
      const format = this.currencyCodeEnabled ? this.moneyFormat.replace(/\{\{[^}]+\}\}/g, `${amount} EUR`) : this.moneyFormat.replace(/\{\{[^}]+\}\}/g, amount);
      return format;
    } catch (error) {
      console.error('BBSondermasse: Error formatting price:', error);
      const amount = price.toFixed(2).replace('.', ',');
      return this.currencyCodeEnabled ? `€${amount} EUR` : `€${amount}`;
    }
  }

  handleFormSubmit(event) {
    const laenge = this.getInputValue('laenge');
    const breite = this.getInputValue('breite');

    if (laenge > 0 || breite > 0) {
      if (!this.validateRange(laenge, breite) || laenge <= 0 || breite <= 0) {
        event.preventDefault();
        event.stopPropagation();
        this.showError();

        const laengeInput = this.querySelector('input[data-type="laenge"]');
        const breiteInput = this.querySelector('input[data-type="breite"]');

        if (laenge <= 0 || !this.isInRange(laengeInput, laenge)) {
          laengeInput?.focus();
        } else if (breite <= 0 || !this.isInRange(breiteInput, breite)) {
          breiteInput?.focus();
        }

        return false;
      }
    }
    return true;
  }

  showError() {
    const errorDiv = this.querySelector('[data-sondermasse="error"]');
    if (errorDiv) errorDiv.style.display = 'block';
  }

  hideError() {
    const errorDiv = this.querySelector('[data-sondermasse="error"]');
    if (errorDiv) errorDiv.style.display = 'none';
  }
}

customElements.define('bb-sondermasse', BBSondermasse);
